import { ERROR_CODES, type PartnerListItem } from '@erp/shared';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { InlineHelp } from '@/features/help';
import { ApiError } from '@/lib/api-client';
import { useBulkDeletePartners } from '../api/use-partners';
import { PartnerRoleBadges } from './partner-role-badges';

interface ProspectBulkDeleteProps {
  selected: PartnerListItem[];
  onDeleted: (ids: string[]) => void;
  onBusyChange: (busy: boolean) => void;
}

export function ProspectBulkDelete({
  selected,
  onDeleted,
  onBusyChange,
}: ProspectBulkDeleteProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const deletion = useBulkDeletePartners();
  useEffect(() => {
    onBusyChange(deletion.isPending);
  }, [deletion.isPending, onBusyChange]);

  return (
    <div className="space-y-3">
      {selected.length === 0 ? null : (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3">
          <p className="mr-auto text-sm" aria-live="polite">
            {t('partners.bulkDelete.selected', { count: selected.length })}
          </p>
          <InlineHelp
            articleId="partners.customer-file"
            anchor="bulk-delete-prospects"
            label={t('partners.bulkDelete.help')}
          />
          <Button
            variant="destructive"
            size="sm"
            disabled={deletion.isPending}
            onClick={() => setOpen(true)}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {t('partners.bulkDelete.action')}
          </Button>
        </div>
      )}
      {deletion.data?.deletedIds.length ? (
        <p role="status" className="text-sm">
          {t('partners.bulkDelete.deleted', { count: deletion.data.deletedIds.length })}
        </p>
      ) : null}
      {deletion.data?.failures.length ? (
        <Alert variant="destructive" role="alert">
          <AlertTitle>
            {t('partners.bulkDelete.failed', { count: deletion.data.failures.length })}
          </AlertTitle>
          <AlertDescription>
            <ul className="mt-2 space-y-1">
              {deletion.data.failures.map(({ partner, error }) => (
                <li key={partner.id} className="break-words">
                  <span className="font-medium">{partner.displayName}</span>
                  {': '}
                  {error instanceof ApiError && error.code === ERROR_CODES.CUSTOMER_HAS_DOCUMENTS
                    ? t('partners.bulkDelete.hasDocuments')
                    : error instanceof ApiError
                      ? error.message
                      : t('partners.bulkDelete.error')}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!deletion.isPending) setOpen(next);
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>{t('partners.bulkDelete.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('partners.bulkDelete.description')}</AlertDialogDescription>
          <p className="text-sm font-medium">
            {t('partners.bulkDelete.selected', { count: selected.length })}
          </p>
          <ul className="max-h-64 space-y-2 overflow-auto">
            {selected.map((partner) => (
              <li key={partner.id} className="space-y-1 break-words">
                <p>
                  {partner.displayName}{' '}
                  <span className="text-xs text-muted-foreground">{partner.partnerNumber}</span>
                </p>
                <PartnerRoleBadges
                  isCustomer={partner.isCustomer}
                  isProspect={partner.isProspect}
                  isSupplier={partner.isSupplier}
                />
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletion.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deletion.isPending || selected.length === 0}
              onClick={(event) => {
                event.preventDefault();
                deletion.mutate(selected, {
                  onSuccess: (result) => {
                    onDeleted(result.deletedIds);
                    setOpen(false);
                  },
                });
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
